import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/api';
import {
  getProviders,
  MICROSOFT_EXPIRED_TEXT,
  MICROSOFT_LOGIN_PATH,
  microsoftLoginErrorText,
  readRememberedUsername,
} from '../api/core';
import { useAuth } from '../auth/AuthContext';
import {
  clearExplicitLogout,
  clearRedirectGuard,
  hasExplicitLogout,
  isRedirectGuardBlocked,
  markRedirectAttempt,
} from '../auth/entraRedirectGuard';
import { FormField } from '../components/FormField';
import { useTheme } from '../hooks/useTheme';

/** Svarer providers-kaldet ikke inden da, vises kodeordsformularen (som ved fejl). */
const PROVIDERS_TIMEOUT_MS = 5000;

/** Vises over knappen, når løkkeværnet har standset den automatiske viderestilling. */
const GUARD_TEXT = 'Vi kunne ikke logge dig ind automatisk.';

/**
 * Query-parametrene, Microsoft-loginet sender browseren tilbage med (kontrakt 0.21.0), og
 * `?login=kode`: servicebrugernes og e2e-scriptenes indgang uden automatisk viderestilling.
 */
function readQuery(search: string) {
  const params = new URLSearchParams(search);
  return {
    ms: params.get('ms'),
    loginError: params.get('login_error'),
    login: params.get('login'),
  };
}

/** Sætter løkkeværnet og sender hele siden videre til Microsoft (automatisk eller ved klik). */
function startMicrosoftLogin(): void {
  markRedirectAttempt();
  window.location.assign(MICROSOFT_LOGIN_PATH);
}

export function LoginPage() {
  // Sat <html data-theme="..."> ellers kun i AppShell (efter login), saa
  // loginsiden viste altid det lyse tema uanset det gemte valg (brief 5).
  useTheme();
  const { isAuthenticated, login, loginWithMicrosoft } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Brugernavnet huskes mellem logins (kontrakt 0.11.0, design afsnit 8.7).
  const [rememberedUsername] = useState(() => readRememberedUsername());
  const [username, setUsername] = useState(rememberedUsername);
  const [password, setPassword] = useState('');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Læst én gang: query'en fjernes fra adressen, så et genindlæs ikke gentager fejlen.
  const [initialQuery] = useState(() => readQuery(location.search));
  // `?ms=ok`: Microsoft-loginet lykkedes hos core, JWT'en skal hentes (engangs-cookie).
  const [exchanging, setExchanging] = useState(initialQuery.ms === 'ok');
  // Microsoft-fejl (login_error eller mislykket token-kald) – vises ovenfor knapperne.
  const [notice, setNotice] = useState<string | null>(() =>
    initialQuery.ms !== 'ok' && initialQuery.loginError !== null
      ? microsoftLoginErrorText(initialQuery.loginError)
      : null,
  );
  // null = GET /api/auth/providers er ikke besvaret endnu.
  const [microsoftEnabled, setMicrosoftEnabled] = useState<boolean | null>(null);
  // `?login=kode`: formularen vises foldet ud fra start.
  const [showForm, setShowForm] = useState(initialQuery.login === 'kode');
  const exchangeStarted = useRef(false);

  // Automatisk viderestilling (0.21.1): beslutningsgrundlaget læses SYNKRONT ved første
  // render, aldrig i en effekt, der når at køre efter automatikken har besluttet sig.
  // useState fanger værdierne én gang, så de ikke forsvinder, når flag og værn ryddes.
  const [entry] = useState(() => {
    // Engangsflaget fra "Log ud" (ryddes ved mount nedenfor).
    const afterLogout = hasExplicitLogout();
    // Automatikken er kun aktuel uden fejl, uden ?ms=ok, uden ?login=kode og efter et
    // session-udløb eller et nyt besøg – ikke lige efter et bevidst "Log ud".
    const automatic =
      initialQuery.loginError === null &&
      initialQuery.ms !== 'ok' &&
      initialQuery.login !== 'kode' &&
      !afterLogout;
    return { afterLogout, automatic, guardBlocked: automatic && isRedirectGuardBlocked() };
  });
  const [guardNotice, setGuardNotice] = useState(entry.guardBlocked);
  // Tilbage fra Microsoft med browserens Tilbage-knap (bfcache): siden står frosset på
  // "Sender dig videre…" og skal i stedet vise knappen.
  const [cameBack, setCameBack] = useState(false);
  const redirectStarted = useRef(false);

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  // Fjerner kun ?ms og ?login_error; ?login=kode og evt. andet bliver stående.
  const stripQuery = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('ms');
    params.delete('login_error');
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true, state: location.state },
    );
  }, [navigate, location.pathname, location.search, location.state]);

  // Ved visning: engangsflaget fra "Log ud" ryddes, og en vist login_error fjernes fra
  // adressen og nulstiller løkkeværnet (fejlen er en bedre forklaring end værnet, og et
  // senere besøg skal kunne forsøge automatikken forfra).
  useEffect(() => {
    if (entry.afterLogout) clearExplicitLogout();
    if (initialQuery.ms !== 'ok' && initialQuery.loginError !== null) {
      clearRedirectGuard();
      stripQuery();
    }
    // Kun ved visning: initialQuery er konstant, og stripQuery må ikke køre igen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hvilke loginmetoder er slået til? Fejl eller timeout = kun kodeordsformularen.
  useEffect(() => {
    if (isAuthenticated || exchanging || microsoftEnabled !== null) return;
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROVIDERS_TIMEOUT_MS);
    getProviders(controller.signal)
      .then((providers) => {
        if (!cancelled) setMicrosoftEnabled(providers?.microsoft === true);
      })
      .catch(() => {
        if (!cancelled) setMicrosoftEnabled(false);
      })
      .finally(() => clearTimeout(timer));
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [isAuthenticated, exchanging, microsoftEnabled]);

  // ?ms=ok: hent JWT'en. Refen sikrer ét kald (cookien er engangsbrug, også i StrictMode).
  useEffect(() => {
    if (!exchanging || isAuthenticated || exchangeStarted.current) return;
    exchangeStarted.current = true;
    loginWithMicrosoft()
      .then(() => navigate('/', { replace: true }))
      .catch((cause) => {
        if (cause instanceof ApiError) {
          // En 401 uden dansk tekst fra serveren må ikke blive til "session udløbet".
          setNotice(
            cause.status === 401 && cause.code === 'http_401'
              ? MICROSOFT_EXPIRED_TEXT
              : cause.messageDa,
          );
        } else {
          setNotice('Der opstod en uventet fejl. Prøv igen.');
        }
        setExchanging(false);
        stripQuery();
      });
  }, [exchanging, isAuthenticated, loginWithMicrosoft, navigate, stripQuery]);

  // Microsoft slået til og intet taler imod: send straks videre. Refen sikrer ét kald
  // (to redirects ville gå af hinanden under StrictMode's dobbelte effekt-kørsel).
  const redirecting =
    microsoftEnabled === true && entry.automatic && !entry.guardBlocked && !cameBack;
  useEffect(() => {
    if (!redirecting || isAuthenticated || redirectStarted.current) return;
    redirectStarted.current = true;
    startMicrosoftLogin();
  }, [redirecting, isAuthenticated]);

  // Tilbage-knappen fra Microsoft genindsætter siden fra bfcache uden at køre effekter:
  // vis i stedet knappen og forklaringen, så siden ikke står fast.
  useEffect(() => {
    if (!redirecting) return;
    function onPageShow(event: PageTransitionEvent) {
      if (!event.persisted) return;
      setCameBack(true);
      setGuardNotice(true);
    }
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [redirecting]);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setNotice(null);
    setGuardNotice(false);
    if (username.trim() === '') {
      setUsernameError('Indtast brugernavn.');
      setError(null);
      return;
    }
    if (password.trim() === '') {
      setUsernameError(null);
      setError('Indtast adgangskoden.');
      return;
    }
    setBusy(true);
    setUsernameError(null);
    setError(null);
    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (cause) {
      if (cause instanceof ApiError) {
        setError(
          cause.status === 401 ? 'Forkert brugernavn eller adgangskode.' : cause.messageDa,
        );
      } else {
        setError('Der opstod en uventet fejl. Prøv igen.');
      }
      // Brugernavnet bliver staaende, saa kun koden skal skrives igen.
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  const waiting = exchanging || microsoftEnabled === null || redirecting;
  const microsoftMode = microsoftEnabled === true;
  const formVisible = microsoftEnabled === false || (microsoftMode && showForm);
  const statusText = exchanging
    ? 'Logger ind…'
    : redirecting
      ? 'Sender dig videre til Microsoft…'
      : 'Indlæser…';
  // Løkkeværnets forklaring hører kun til, når Microsoft-login faktisk er i spil.
  const message = notice ?? (microsoftMode && guardNotice ? GUARD_TEXT : null);

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <span className="logo">HBC</span>
          <h1>HBCare Planner</h1>
        </div>

        {waiting && (
          <p className="login-intro" role="status">
            {statusText}
          </p>
        )}

        {!waiting && (
          <p className="login-intro">
            {microsoftMode
              ? 'Log ind med din Microsoft-konto.'
              : 'Log ind med dit brugernavn og den fælles adgangskode.'}
          </p>
        )}

        {!waiting && message && (
          <p className="field-error" role="alert">
            {message}
          </p>
        )}

        {!waiting && microsoftMode && (
          <button
            type="button"
            className="btn btn-primary full-width"
            autoFocus={!showForm}
            onClick={startMicrosoftLogin}
          >
            Log ind med Microsoft
          </button>
        )}

        {!waiting && microsoftMode && !showForm && (
          <button
            type="button"
            className="login-link"
            aria-expanded={false}
            aria-controls="login-form"
            onClick={() => {
              setNotice(null);
              setGuardNotice(false);
              setShowForm(true);
            }}
          >
            Log ind med servicekonto
          </button>
        )}

        {!waiting && formVisible && (
          <form
            id="login-form"
            className={`login-form${microsoftMode ? ' login-form-divided' : ''}`}
            onSubmit={onSubmit}
          >
            <FormField
              label="Brugernavn"
              value={username}
              onChange={(value) => {
                setUsername(value);
                if (usernameError) setUsernameError(null);
              }}
              autoFocus={rememberedUsername === ''}
              autoComplete="username"
              placeholder="fx test"
              error={usernameError}
              disabled={busy}
            />

            <FormField
              label="Adgangskode"
              type="password"
              value={password}
              onChange={(value) => {
                setPassword(value);
                if (error) setError(null);
              }}
              autoFocus={rememberedUsername !== ''}
              autoComplete="current-password"
              error={error}
              disabled={busy}
            />

            {/* Ved siden af Microsoft-knappen er den ikke den primære handling. */}
            <button
              type="submit"
              className={`btn ${microsoftMode ? 'btn-secondary' : 'btn-primary'} full-width`}
              disabled={busy}
            >
              {busy ? 'Logger ind…' : 'Log ind'}
            </button>
          </form>
        )}

        <p className="login-footer">HB-Cares driftsportal</p>
      </div>
    </div>
  );
}
